/**
 * Splits items into `count` columns in reading order (item 0 at the top of
 * column 0, each column a consecutive run) so that the tallest column is as
 * short as possible. kinklist-v2 used a greedy rule that could leave the last
 * column much longer than the rest; this solves the ordered partition exactly
 * (dynamic programming, fine for the few dozen categories a list has).
 *
 * Returns item indices per column; trailing columns may be empty when there
 * are fewer items than columns.
 */
export function splitColumns(heights: readonly number[], count: number): number[][] {
  const n = heights.length;
  const columns = Math.max(1, Math.floor(count));
  const k = Math.min(columns, Math.max(n, 1));
  const prefix = [0];
  for (const h of heights) prefix.push(at(prefix, prefix.length - 1) + h);

  // cost[j][i]: minimal tallest column placing the first i items into j columns;
  // cut[j][i]: where the j-th column starts in that solution.
  const cost = Array.from({ length: k + 1 }, (_, j) =>
    Array.from({ length: n + 1 }, (_, i) => (j === 0 && i === 0 ? 0 : Infinity)),
  );
  const cut = Array.from({ length: k + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let j = 1; j <= k; j++) {
    const row = at(cost, j);
    const prev = at(cost, j - 1);
    const cuts = at(cut, j);
    for (let i = 0; i <= n; i++) {
      for (let p = 0; p <= i; p++) {
        const value = Math.max(at(prev, p), at(prefix, i) - at(prefix, p));
        // `<=` keeps the latest cut on ties, so earlier columns fill first.
        if (value <= at(row, i)) {
          row[i] = value;
          cuts[i] = p;
        }
      }
    }
  }

  const result: number[][] = Array.from({ length: columns }, () => []);
  let end = n;
  for (let j = k; j >= 1; j--) {
    const start = at(at(cut, j), end);
    at(result, j - 1).push(...Array.from({ length: end - start }, (_, i) => start + i));
    end = start;
  }
  return result;
}

/** Height of the tallest column for a split. */
export function tallestColumn(
  heights: readonly number[],
  split: readonly (readonly number[])[],
): number {
  return Math.max(0, ...split.map((column) => column.reduce((sum, i) => sum + at(heights, i), 0)));
}

/** Checked array access: these indices are always in range by construction. */
function at<T>(list: readonly T[], index: number): T {
  const value = list[index];
  if (value === undefined) throw new RangeError(`Index ${String(index)} out of range`);
  return value;
}
